import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Lightbulb, Star, MapPin, Share2, Globe, Gem, ChevronRight, Navigation, Coffee, Hotel, Camera, Ship, Plane } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(res.data);
    } catch (err) {
      console.error('Error fetching post:', err);
      setError('Post not found');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.introduction,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="blog-post-page" data-testid="blog-post-page">
        <div className="blog-post-loading">
          <div className="loading-spinner"></div>
          <p>Loading story...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-post-page" data-testid="blog-post-page">
        <div className="blog-post-error">
          <Globe size={48} />
          <h2>Story Not Found</h2>
          <p>The tale you're looking for doesn't exist or has been moved.</p>
          <Link to="/jnt-tales" className="back-to-tales-btn">
            <ArrowLeft size={18} />
            Back to JNT Tales
          </Link>
        </div>
      </div>
    );
  }

  const relatedCities = post.related_cities || [];
  const country = post.country;

  return (
    <div className="blog-post-page" data-testid="blog-post-page">
      {/* Hero Section */}
      <section className="blog-post-hero">
        {post.featured_image && (
          <img 
            src={post.featured_image} 
            alt={post.image_alt || post.title} 
            className="blog-post-hero-image"
          />
        )}
        <div className="blog-post-hero-overlay"></div>
        <div className="blog-post-hero-content">
          <div className="blog-post-continent-badge">{post.continent}</div>
          <h1 className="blog-post-title">{post.title}</h1>
          {country && (
            <Link to={`/jnt-tales/country/${country.slug}`} className="blog-post-country-link">
              <MapPin size={16} />
              {country.name}
            </Link>
          )}
          <Link to="/jnt-tales" className="blog-post-back-link" data-testid="back-to-tales">
            <ArrowLeft size={18} />
            <span>Back to JNT Tales</span>
          </Link>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <div className="blog-post-wrapper">
        {/* Main Content */}
        <article className="blog-post-content">
          <div className="blog-post-container">
            {/* Introduction */}
            <div className="blog-post-intro">
              <p>{post.introduction}</p>
            </div>

            {/* Share Button */}
            <button 
              className="blog-post-share-btn"
              onClick={handleShare}
              data-testid="share-button"
            >
              <Share2 size={18} />
              <span>Share this Tale</span>
            </button>

            {/* Main Content */}
            {post.content && (
              <div className="blog-post-body">
                <ReactMarkdown>{post.content}</ReactMarkdown>
              </div>
            )}

            {/* Getting Around Section */}
            {post.getting_around && (
              <div className="blog-post-section" data-testid="getting-around-section">
                <div className="blog-post-body">
                  <ReactMarkdown>{post.getting_around}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Curated Experiences Section */}
            {post.curated_experiences && (
              <div className="blog-post-section" data-testid="curated-experiences-section">
                <div className="blog-post-body">
                  <ReactMarkdown>{post.curated_experiences}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Rail & Cruise Tours Section */}
            {post.rail_cruise_tours && (
              <div className="blog-post-section" data-testid="rail-cruise-section">
                <div className="blog-post-body">
                  <ReactMarkdown>{post.rail_cruise_tours}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Indian Traveler Guide Section */}
            {post.indian_traveler_guide && (
              <div className="blog-post-section indian-guide-section" data-testid="indian-guide-section">
                <div className="blog-post-body">
                  <ReactMarkdown>{post.indian_traveler_guide}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Info Cards */}
            <div className="blog-post-info-cards">
              {post.best_time && (
                <div className="info-card" data-testid="best-time-card">
                  <div className="info-card-icon">
                    <Clock size={24} />
                  </div>
                  <div className="info-card-content">
                    <h4>Best Time to Visit</h4>
                    <p>{post.best_time}</p>
                  </div>
                </div>
              )}

              {post.insider_tip && (
                <div className="info-card tip-card" data-testid="insider-tip-card">
                  <div className="info-card-icon">
                    <Lightbulb size={24} />
                  </div>
                  <div className="info-card-content">
                    <h4>Insider Tip</h4>
                    <p>{post.insider_tip}</p>
                  </div>
                </div>
              )}

              {post.jnt_experience && (
                <div className="info-card jnt-card" data-testid="jnt-experience-card">
                  <div className="info-card-icon">
                    <Star size={24} />
                  </div>
                  <div className="info-card-content">
                    <h4>JNT Experience</h4>
                    <p>{post.jnt_experience}</p>
                  </div>
                </div>
              )}

              {post.hidden_gems && (
                <div className="blog-post-section gems-section" data-testid="hidden-gems-section">
                  <div className="blog-post-body">
                    <ReactMarkdown>{post.hidden_gems}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>

            {/* CTA */}
            <div className="blog-post-cta">
              <h3>Ready to Experience {country?.name || post.continent}?</h3>
              <p>Let our travel experts craft your perfect journey</p>
              <Link to="/design-journey" className="blog-cta-btn" data-testid="post-cta-btn">
                Design My Journey
              </Link>
            </div>
          </div>
        </article>

        {/* Sidebar - Related Cities */}
        {relatedCities.length > 0 && (
          <aside className="blog-post-sidebar">
            <div className="sidebar-section">
              <h3>
                <MapPin size={18} />
                More in {country?.name || post.continent}
              </h3>
              <ul className="sidebar-cities-list">
                {relatedCities.map((city) => (
                  <li key={city.id}>
                    <Link to={`/jnt-tales/${city.slug}`} className="sidebar-city-link">
                      {city.featured_image && (
                        <img src={city.featured_image} alt={city.title} />
                      )}
                      <span>{city.title}</span>
                      <ChevronRight size={14} />
                    </Link>
                  </li>
                ))}
              </ul>
              {country && (
                <Link to={`/jnt-tales/country/${country.slug}`} className="sidebar-view-all">
                  View all destinations in {country.name}
                </Link>
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default BlogPostPage;
